import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { Plus, Trash } from "lucide-react";

import { BadgeSelector } from "@/components/fields/badge-selector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTags } from "@/contexts/tags-context";

export function EventTagsField({ id, inputProps }: AutoFormFieldProps) {
  const form = useFormContext();
  const value: string[] = useWatch({ name: id }) ?? [];
  const { tags } = useTags();

  const setValue = (newValues: string[]) => {
    form.setValue(id, newValues, { shouldDirty: true });
    inputProps.onChange({
      target: { name: id, value: newValues },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const badgeItems = tags?.map((t) => ({ id: t.value, name: t.label })) ?? [];
  const badgeValues = value.map((v) => ({ id: v, name: v }));

  return (
    <div className="flex flex-col text-foreground w-full">
      {badgeItems.length > 0 && (
        <div className="flex flex-row space-x-2 flex-wrap">
          {badgeItems.map((item) => (
            <BadgeSelector
              key={item.id}
              item={item}
              values={badgeValues}
              onChange={(items) =>
                setValue(items.map((i: { id: string }) => i.id))
              }
            />
          ))}
        </div>
      )}
      {value.map((item, index) => (
        <div key={index} className="flex flex-row space-x-2 w-full items-end">
          <Input
            className="text-foreground"
            defaultValue={item}
            onBlur={(e) => {
              const newValues = [...value];
              newValues[index] = e.target.value;
              setValue(newValues);
            }}
          />
          <Button
            variant="secondary"
            size="icon"
            type="button"
            className="hover:bg-zinc-300 hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-white dark:text-black dark:hover:bg-zinc-300 dark:hover:text-black dark:hover:ring-0 dark:hover:ring-offset-0 dark:focus-visible:ring-0 dark:focus-visible:ring-offset-0"
            onClick={() => setValue(value.filter((_, i) => i !== index))}
          >
            <Trash className="size-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        onClick={() => setValue([...value, ""])}
        className="mt-4 flex items-center text-foreground"
      >
        <Plus className="mr-2" size={16} />
        Add
      </Button>
    </div>
  );
}
